import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import scenariosData from './pass_scoring_matrix.json';

function AssessmentApp() {
  const [currentScenarioIndex, setCurrentScenarioIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [currentRatings, setCurrentRatings] = useState({});
  const [loading, setLoading] = useState(false);
  const [assessmentId, setAssessmentId] = useState(null);
  const [user, setUser] = useState(null);
  const [initializing, setInitializing] = useState(true);
  const [showInstructions, setShowInstructions] = useState(true);

  const scenarios = scenariosData.scenarios;
  const currentScenario = scenarios[currentScenarioIndex];
  const progress = ((currentScenarioIndex + 1) / scenarios.length) * 100;

  // PASS Brand Colors
  const brandColors = {
    deepForestGreen: '#0B3D2E',
    teal: '#2A9D8F',
    navy: '#1D3557',
    lightBackground: '#f8faf9',
    cardBackground: '#ffffff',
    textPrimary: '#333333',
    textSecondary: '#666666'
  };

  useEffect(() => {
    checkUser();
  }, []);

  useEffect(() => {
    if (currentScenario) {
      loadCurrentRatings();
    }
  }, [currentScenarioIndex, currentScenario]);

  const checkUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      // Check user profile — admin redirect + payment gate
      const { data: userRow } = await supabase
        .from('users')
        .select('is_admin, payment_status')
        .eq('id', user.id)
        .single();

      // Admin → admin portal
      if (userRow && userRow.is_admin === true) {
        window.location.href = '/admin';
        return;
      }

      // Payment gate — must have valid payment_status to access assessment
      const hasAccess = userRow && (
        userRow.payment_status === 'paid' ||
        userRow.payment_status === 'free_admin' ||
        userRow.payment_status === 'coupon'
      );

      if (!hasAccess) {
        // No payment on record — redirect to landing page
        await supabase.auth.signOut();
        window.location.href = '/?error=payment_required';
        return;
      }

      setUser(user);
      await loadOrCreateAssessment(user.id);
    } else {
      window.location.href = '/login';
    }
    setInitializing(false);
  };

  const loadOrCreateAssessment = async (userId) => {
    const { data: existing } = await supabase
      .from('assessments')
      .select('*')
      .eq('user_id', userId)
      .eq('status', 'in_progress')
      .single();

    if (existing) {
      setAssessmentId(existing.id);
      const scenarioIndex = Math.max(0, (existing.current_scenario || 1) - 1);
      setCurrentScenarioIndex(scenarioIndex);
      await loadResponses(existing.id);
      // If they've already started, skip instructions
      if (scenarioIndex > 0) {
        setShowInstructions(false);
      }
    } else {
      const { data: newAssessment } = await supabase
        .from('assessments')
        .insert([{ user_id: userId }])
        .select()
        .single();
      
      if (newAssessment) {
        setAssessmentId(newAssessment.id);
      }
    }
  };

  const loadResponses = async (assessmentId) => {
    const { data: savedResponses } = await supabase
      .from('responses')
      .select('scenario_id, option_ratings')
      .eq('assessment_id', assessmentId);

    if (savedResponses) {
      const responsesMap = {};
      savedResponses.forEach(r => {
        responsesMap[r.scenario_id] = r.option_ratings;
      });
      setResponses(responsesMap);
    }
  };

  const loadCurrentRatings = () => {
    if (responses[currentScenario.id]) {
      setCurrentRatings(responses[currentScenario.id]);
    } else {
      const emptyRatings = {};
      currentScenario.options.forEach(option => {
        emptyRatings[option.id] = 0;
      });
      setCurrentRatings(emptyRatings);
    }
  };

  // AUTO-TRIGGER: Call Netlify function to generate report
  const triggerReportGeneration = async (userId) => {
    console.log('PASS: Triggering report generation for user:', userId);
    try {
      const response = await fetch('/.netlify/functions/generate-report-v2-background', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: userId })
      });
      console.log('PASS: Report generation triggered, status:', response.status);
    } catch (error) {
      console.error('PASS: Error triggering report generation:', error);
    }
  };

  const handleRatingChange = (optionId, rating) => {
    setCurrentRatings({
      ...currentRatings,
      [optionId]: rating
    });
  };

  const isScenarioComplete = () => {
    return currentScenario.options.every(option => 
      currentRatings[option.id] && currentRatings[option.id] > 0
    );
  };

  const handleNext = async () => {
    if (!isScenarioComplete()) {
      alert('Please rate all options before continuing.');
      return;
    }

    setLoading(true);

    await supabase
      .from('responses')
      .upsert([{
        assessment_id: assessmentId,
        scenario_id: currentScenario.id,
        option_ratings: currentRatings,
        selected_option: null
      }], {
        onConflict: 'assessment_id,scenario_id'
      });

    setResponses({
      ...responses,
      [currentScenario.id]: currentRatings
    });

    if (currentScenarioIndex < scenarios.length - 1) {
      await supabase
        .from('assessments')
        .update({ current_scenario: currentScenarioIndex + 2 })
        .eq('id', assessmentId);

      setCurrentScenarioIndex(currentScenarioIndex + 1);
    } else {
      // ASSESSMENT COMPLETE
      await supabase
        .from('assessments')
        .update({ 
          status: 'completed',
          completed_at: new Date().toISOString()
        })
        .eq('id', assessmentId);

      // AUTO-TRIGGER: Fire report generation before redirect
      console.log('PASS: Assessment completed, triggering report generation...');
      if (user && user.id) {
        await triggerReportGeneration(user.id);
      }

      window.location.href = '/results';
    }

    setLoading(false);
  };

  const handlePrevious = () => {
    if (currentScenarioIndex > 0) {
      setCurrentScenarioIndex(currentScenarioIndex - 1);
    }
  };

  const handleSaveAndExit = async () => {
    if (Object.values(currentRatings).some(rating => rating > 0)) {
      await supabase
        .from('responses')
        .upsert([{
          assessment_id: assessmentId,
          scenario_id: currentScenario.id,
          option_ratings: currentRatings,
          selected_option: null
        }], {
          onConflict: 'assessment_id,scenario_id'
        });
    }

    if (assessmentId) {
      await supabase
        .from('assessments')
        .update({ current_scenario: currentScenarioIndex + 1 })
        .eq('id', assessmentId);
    }
    window.location.href = '/';
  };

  const handleBeginAssessment = () => {
    setShowInstructions(false);
  };

  // Inline styles for branding
  const styles = {
    container: {
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: brandColors.lightBackground,
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
    },
    header: {
      backgroundColor: '#FFFFFF',
      borderBottom: '3px solid #2A9D8F',
      padding: '16px 24px',
      position: 'sticky',
      top: 0,
      zIndex: 100,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
    },
    headerContent: {
      maxWidth: '1000px',
      margin: '0 auto',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    logo: {
      height: '40px'
    },
    progressInfo: {
      fontSize: '14px',
      color: brandColors.deepForestGreen,
      fontWeight: 500
    },
    progressBar: {
      height: '4px',
      backgroundColor: '#E8ECF0',
      marginTop: '12px',
      borderRadius: '2px',
      overflow: 'hidden'
    },
    progressFill: {
      height: '100%',
      backgroundColor: brandColors.teal,
      transition: 'width 0.3s ease'
    },
    main: {
      flex: 1,
      padding: '32px 20px',
      maxWidth: '900px',
      margin: '0 auto',
      width: '100%'
    },
    scenarioCard: {
      background: brandColors.cardBackground,
      borderRadius: '12px',
      padding: '32px',
      boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
      marginBottom: '24px'
    },
    situation: {
      fontSize: '1.1rem',
      lineHeight: 1.7,
      color: brandColors.textPrimary,
      marginBottom: '28px',
      paddingBottom: '24px',
      borderBottom: `1px solid #e8e8e8`
    },
    ratingPrompt: {
      fontSize: '0.95rem',
      color: brandColors.textSecondary,
      marginBottom: '20px',
      textAlign: 'center'
    },
    ratingScale: {
      fontSize: '0.85rem',
      color: brandColors.textSecondary,
      display: 'block',
      marginTop: '4px'
    },
    ratingItem: {
      backgroundColor: '#f8f9fa',
      borderRadius: '8px',
      padding: '16px 20px',
      marginBottom: '12px'
    },
    ratingItemHeader: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
      marginBottom: '12px'
    },
    optionLetter: {
      backgroundColor: brandColors.teal,
      color: 'white',
      width: '28px',
      height: '28px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '14px',
      fontWeight: 600,
      flexShrink: 0
    },
    optionText: {
      fontSize: '0.95rem',
      color: brandColors.textPrimary,
      lineHeight: 1.5
    },
    ratingButtons: {
      display: 'flex',
      gap: '8px',
      justifyContent: 'center'
    },
    ratingButton: {
      width: '44px',
      height: '44px',
      border: '2px solid #ddd',
      borderRadius: '8px',
      backgroundColor: 'white',
      fontSize: '16px',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.15s ease',
      color: brandColors.textPrimary,
      outline: 'none',
      WebkitAppearance: 'none',
      MozAppearance: 'none',
      appearance: 'none',
      textDecoration: 'none',
      boxShadow: 'none'
    },
    ratingButtonSelected: {
      backgroundColor: brandColors.teal,
      borderColor: brandColors.teal,
      color: 'white'
    },
    navigation: {
      display: 'flex',
      justifyContent: 'space-between',
      gap: '12px',
      flexWrap: 'wrap'
    },
    navButton: {
      padding: '12px 24px',
      borderRadius: '8px',
      fontSize: '1rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      border: 'none',
      outline: 'none',
      WebkitAppearance: 'none',
      MozAppearance: 'none',
      appearance: 'none',
      textDecoration: 'none'
    },
    navButtonPrimary: {
      backgroundColor: brandColors.teal,
      color: 'white'
    },
    navButtonSecondary: {
      backgroundColor: 'white',
      color: brandColors.textPrimary,
      border: `1px solid #ddd`
    },
    navButtonDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed'
    },
    footer: {
      backgroundColor: brandColors.cardBackground,
      padding: '16px 20px',
      textAlign: 'center',
      borderTop: '1px solid #e8e8e8',
      fontSize: '0.9rem',
      color: brandColors.textSecondary
    },
    // Instructions screen styles
    instructionsCard: {
      background: brandColors.cardBackground,
      borderRadius: '12px',
      padding: '48px',
      boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
      maxWidth: '700px',
      margin: '0 auto',
      textAlign: 'center'
    },
    instructionsTitle: {
      fontSize: '1.75rem',
      fontWeight: 700,
      color: brandColors.deepForestGreen,
      marginBottom: '24px'
    },
    instructionsList: {
      textAlign: 'left',
      maxWidth: '500px',
      margin: '0 auto 32px',
      lineHeight: 1.8
    },
    instructionItem: {
      marginBottom: '16px',
      color: brandColors.textPrimary,
      fontSize: '1rem'
    },
    beginButton: {
      backgroundColor: brandColors.teal,
      color: 'white',
      border: 'none',
      padding: '16px 48px',
      fontSize: '1.1rem',
      fontWeight: 600,
      borderRadius: '8px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      boxShadow: '0 4px 14px rgba(42, 157, 143, 0.3)',
      outline: 'none',
      WebkitAppearance: 'none',
      MozAppearance: 'none',
      appearance: 'none'
    },
    timeEstimate: {
      marginTop: '20px',
      fontSize: '0.9rem',
      color: brandColors.textSecondary
    }
  };

  // Global CSS to override visited link styling on buttons
  const globalStyles = `
    button, button:visited, button:link, button:active {
      text-decoration: none !important;
      border-color: inherit;
    }
    .rating-btn {
      border: 2px solid #ddd !important;
      text-decoration: none !important;
    }
    .rating-btn:visited {
      border: 2px solid #ddd !important;
      color: #333333 !important;
      text-decoration: none !important;
    }
    .rating-btn.selected {
      border: 2px solid #2A9D8F !important;
      background-color: #2A9D8F !important;
      color: white !important;
    }
    .rating-btn.selected:visited {
      border: 2px solid #2A9D8F !important;
      background-color: #2A9D8F !important;
      color: white !important;
    }
  `;

  if (initializing) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        fontSize: '18px',
        color: brandColors.textPrimary,
        fontFamily: "'Inter', -apple-system, sans-serif"
      }}>
        Loading assessment...
      </div>
    );
  }

  // Instructions Screen
  if (showInstructions) {
    return (
      <div style={styles.container}>
        <style>{globalStyles}</style>
        <header style={styles.header}>
          <div style={styles.headerContent}>
            <img src="/pass-logo.png" alt="PASS Profile 3.0" style={styles.logo} />
          </div>
        </header>

        <main style={styles.main}>
          <div style={styles.instructionsCard}>
            <h1 style={styles.instructionsTitle}>Welcome to Your PASS Profile Assessment</h1>
            
            <div style={styles.instructionsList}>
              <p style={styles.instructionItem}>
                <strong>60 workplace scenarios</strong> — Each scenario describes a realistic work situation.
              </p>
              <p style={styles.instructionItem}>
                <strong>Rate each response</strong> — For each scenario, rate how likely you would respond each way on a scale of 1-5.
              </p>
              <p style={styles.instructionItem}>
                <strong>There are no right or wrong answers</strong> — This measures how you naturally communicate, not how you "should" respond.
              </p>
              <p style={styles.instructionItem}>
                <strong>Save and resume anytime</strong> — Your progress is automatically saved. You can exit and return later.
              </p>
            </div>

            <button 
              onClick={handleBeginAssessment}
              style={styles.beginButton}
            >
              Begin Assessment
            </button>
            
            <p style={styles.timeEstimate}>
              Estimated time: about 45 minutes
            </p>
          </div>
        </main>
      </div>
    );
  }

  if (!currentScenario) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        fontSize: '18px',
        color: brandColors.textPrimary,
        fontFamily: "'Inter', -apple-system, sans-serif"
      }}>
        Loading scenario...
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <style>{globalStyles}</style>
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <img src="/pass-logo.png" alt="PASS Profile 3.0" style={styles.logo} />
          <div style={styles.progressInfo}>
            Scenario {currentScenarioIndex + 1} of {scenarios.length}
          </div>
        </div>
        <div style={styles.progressBar}>
          <div style={{...styles.progressFill, width: `${progress}%`}} />
        </div>
      </header>

      <main style={styles.main}>
        <div style={styles.scenarioCard}>
          {/* Domain label intentionally removed per P1 requirements */}
          
          <div style={styles.situation}>
            <p>{currentScenario.situation}</p>
          </div>

          <div>
            <p style={styles.ratingPrompt}>
              Rate each response: How likely would you respond this way?
              <span style={styles.ratingScale}>
                1 = Not like me at all | 5 = Very much like me
              </span>
            </p>

            <div>
              {currentScenario.options.map((option) => (
                <div key={option.id} style={styles.ratingItem}>
                  <div style={styles.ratingItemHeader}>
                    <span style={styles.optionLetter}>{option.id}</span>
                    <span style={styles.optionText}>{option.text}</span>
                  </div>
                  <div style={styles.ratingButtons}>
                    {[1, 2, 3, 4, 5].map(value => (
                      <button
                        key={value}
                        type="button"
                        className={`rating-btn ${currentRatings[option.id] === value ? 'selected' : ''}`}
                        style={{
                          ...styles.ratingButton,
                          ...(currentRatings[option.id] === value ? styles.ratingButtonSelected : {})
                        }}
                        onClick={() => handleRatingChange(option.id, value)}
                        disabled={loading}
                      >
                        {value}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={styles.navigation}>
          <button 
            type="button"
            style={{
              ...styles.navButton,
              ...styles.navButtonSecondary,
              ...(currentScenarioIndex === 0 || loading ? styles.navButtonDisabled : {})
            }}
            onClick={handlePrevious}
            disabled={currentScenarioIndex === 0 || loading}
          >
            ← Previous
          </button>
          <button 
            type="button"
            style={{
              ...styles.navButton,
              ...styles.navButtonSecondary
            }}
            onClick={handleSaveAndExit}
            disabled={loading}
          >
            Save & Exit
          </button>
          <button 
            type="button"
            style={{
              ...styles.navButton,
              ...styles.navButtonPrimary,
              ...(!isScenarioComplete() || loading ? styles.navButtonDisabled : {})
            }}
            onClick={handleNext}
            disabled={!isScenarioComplete() || loading}
          >
            {currentScenarioIndex === scenarios.length - 1 ? 'Complete' : 'Next'} →
          </button>
        </div>
      </main>

      <footer style={styles.footer}>
        <p>Take your time. Rate each option honestly based on how you naturally respond.</p>
      </footer>
    </div>
  );
}

export default AssessmentApp;
